import dayjs from "dayjs";

export type RiskLevel = "Critical" | "High" | "Medium" | "Low";
export type AssessmentKind = "exact" | "close" | "different" | "none";

export const NOT_SUPPLIED = "Not supplied";

// Presentation-only classification; the score itself is never altered.
export const getRisk = (score: number): RiskLevel => {
  if (score >= 95) return "Critical";
  if (score >= 90) return "High";
  if (score >= 80) return "Medium";
  return "Low";
};

export const getSimilarityAssessment = (
  similarity: number | null
): { label: string; kind: AssessmentKind } => {
  if (similarity === null || similarity === undefined) {
    return { label: NOT_SUPPLIED, kind: "none" };
  }
  if (similarity >= 1) return { label: "Exact / normalised match", kind: "exact" };
  if (similarity >= 0.9) return { label: "Close match", kind: "close" };
  return { label: "Different", kind: "different" };
};

// Similarity is on a 0-1 scale; shown as a percentage.
export const formatSimilarityPercent = (similarity: number | null): string =>
  similarity === null || similarity === undefined
    ? "-"
    : `${Math.round(similarity * 100)}%`;

export const formatScore = (score: number): string => `${Math.round(score)}%`;

export const formatDob = (value: string | null): string => {
  if (!value) return NOT_SUPPLIED;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : value;
};

export const formatAuditDate = (utc: string): string => {
  const parsed = dayjs(utc);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY") : utc;
};

export const formatAuditDateTime =(utc: string): string => {
  const parsed = dayjs(utc);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY HH:mm") : utc;
};

export const displayValue = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === "" ? NOT_SUPPLIED : String(value);

export const joinName = (first: string | null, last: string | null): string =>
  [first, last].filter(Boolean).join(" ") || NOT_SUPPLIED;

export interface DiffSegment {
  text: string;
  changed: boolean;
}

const sameChar = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const toSegments = (chars: string[], changed: boolean[]): DiffSegment[] => {
  const segments: DiffSegment[] = [];
  chars.forEach((ch, i) => {
    const last = segments[segments.length - 1];
    if (last && last.changed === changed[i]) last.text += ch;
    else segments.push({ text: ch, changed: changed[i] });
  });
  return segments;
};

// Character-level diff (longest common subsequence). Case is ignored and whitespace is never
// flagged, so only real differences are highlighted (e.g. "SN7 7EL" vs "SN77EL" shows none).
export const diffValues = (a: string, b: string): { a: DiffSegment[]; b: DiffSegment[] } => {
  const x = Array.from(a);
  const y = Array.from(b);
  const lcs: number[][] = Array.from({ length: x.length + 1 }, () => new Array(y.length + 1).fill(0));
  for (let i = x.length - 1; i >= 0; i--) {
    for (let j = y.length - 1; j >= 0; j--) {
      lcs[i][j] = sameChar(x[i], y[j]) ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const changedA = x.map(() => true);
  const changedB = y.map(() => true);
  let i = 0;
  let j = 0;
  while (i < x.length && j < y.length) {
    if (sameChar(x[i], y[j])) {
      changedA[i++] = false;
      changedB[j++] = false;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) i++;
    else j++;
  }
  x.forEach((ch, k) => {
    if (/\s/.test(ch)) changedA[k] = false;
  });
  y.forEach((ch, k) => {
    if (/\s/.test(ch)) changedB[k] = false;
  });
  return { a: toSegments(x, changedA), b: toSegments(y, changedB) };
};
